import { useTranslation } from "react-i18next";
import SiteLayout from "@/components/feature/SiteLayout";
import PageBanner from "@/components/feature/PageBanner";
import AboutSubNav from "@/components/feature/AboutSubNav";

interface LocationLabels {
  address: string;
  tel: string;
  fax: string;
  email: string;
  subway: string;
  bus: string;
  parking: string;
}

interface LocationData {
  address: string;
  postalCode: string;
  tel: string;
  fax: string;
  email: string;
  mapEmbed: string;
  subway: string;
  bus: string;
  parking: string;
  contactTitle: string;
  transportTitle: string;
  mapTitle: string;
  labels: LocationLabels;
}

export default function AboutLocationPage() {
  const { t } = useTranslation();
  const location = t("about.location", { returnObjects: true }) as unknown as LocationData;

  return (
    <SiteLayout navVariant="solid">
      <PageBanner
        title={t("about.banners.location.title")}
        subtitle={t("about.banners.location.subtitle")}
        breadcrumb={[
          { label: t("about.companyLabel"), to: "/about/overview" },
          { label: t("about.banners.location.title") },
        ]}
        bgImage="https://readdy.ai/api/search-image?query=Modern%20clean%20industrial%20manufacturing%20facility%20interior%20with%20organized%20production%20floor%20and%20natural%20light%20streaming%20through%20windows%2C%20professional%20engineering%20environment%2C%20warm%20industrial%20aesthetic%2C%20soft%20ambient%20lighting%2C%20crisp%20architectural%20photography&width=1600&height=680&seq=krubber-about-banner&orientation=landscape"
      />
      <AboutSubNav />

      <section className="py-16 md:py-20">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
          <div className="flex flex-col lg:flex-row gap-12">
            <div className="lg:w-[480px] flex-shrink-0">
              <h2 className="text-2xl md:text-3xl font-heading font-extrabold text-foreground-950">
                {location.contactTitle}
              </h2>
              <div className="mt-8 p-8 rounded-2xl bg-background-100 border border-background-200/70">
                <ul className="flex flex-col gap-6">
                  <li className="flex items-start gap-4">
                    <span className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-2-line text-primary-600" />
                    </span>
                    <div>
                      <p className="text-xs font-bold text-foreground-500 uppercase tracking-wide">{location.labels.address}</p>
                      <p className="mt-1 text-sm font-semibold text-foreground-900 leading-relaxed">
                        {location.address}
                      </p>
                      <p className="text-xs text-foreground-500 mt-1">Zip) {location.postalCode}</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-4">
                    <span className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-phone-line text-primary-600" />
                    </span>
                    <div>
                      <p className="text-xs font-bold text-foreground-500 uppercase tracking-wide">{location.labels.tel}</p>
                      <a href={`tel:${location.tel}`} className="mt-1 text-sm font-semibold text-foreground-900 hover:text-primary-600 transition-colors cursor-pointer">
                        {location.tel}
                      </a>
                    </div>
                  </li>
                  <li className="flex items-start gap-4">
                    <span className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-printer-line text-primary-600" />
                    </span>
                    <div>
                      <p className="text-xs font-bold text-foreground-500 uppercase tracking-wide">{location.labels.fax}</p>
                      <p className="mt-1 text-sm font-semibold text-foreground-900">{location.fax}</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-4">
                    <span className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-primary-600" />
                    </span>
                    <div>
                      <p className="text-xs font-bold text-foreground-500 uppercase tracking-wide">{location.labels.email}</p>
                      <a href={`mailto:${location.email}`} className="mt-1 text-sm font-semibold text-foreground-900 hover:text-primary-600 transition-colors cursor-pointer">
                        {location.email}
                      </a>
                    </div>
                  </li>
                </ul>
              </div>

              <h2 className="text-xl font-heading font-extrabold text-foreground-950 mt-8">
                {location.transportTitle}
              </h2>
              <div className="mt-4 p-6 rounded-xl bg-background-100 border border-background-200/70">
                <div className="flex items-start gap-3 mb-4">
                  <span className="w-8 h-8 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-train-line text-accent-600 text-sm" />
                  </span>
                  <div>
                    <p className="text-xs font-bold text-foreground-500">{location.labels.subway}</p>
                    <p className="text-sm text-foreground-800 mt-0.5">{location.subway}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 mb-4">
                  <span className="w-8 h-8 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-bus-line text-accent-600 text-sm" />
                  </span>
                  <div>
                    <p className="text-xs font-bold text-foreground-500">{location.labels.bus}</p>
                    <p className="text-sm text-foreground-800 mt-0.5">{location.bus}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="w-8 h-8 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-car-line text-accent-600 text-sm" />
                  </span>
                  <div>
                    <p className="text-xs font-bold text-foreground-500">{location.labels.parking}</p>
                    <p className="text-sm text-foreground-800 mt-0.5">{location.parking}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <h2 className="text-2xl md:text-3xl font-heading font-extrabold text-foreground-950">
                {location.mapTitle}
              </h2>
              <div className="mt-8 rounded-2xl overflow-hidden border border-background-200/70 h-[600px]">
                <div
                  className="w-full h-full"
                  dangerouslySetInnerHTML={{ __html: location.mapEmbed }}
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}